import { FC, useCallback, useEffect, useState } from "react";
import { Icon, Loader, Card, Grid, Message } from "semantic-ui-react";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { getUserAgent, toastError } from "../../../app_shared_functions";
import FetchAPI from "../../../api/FetchAPI";
import {
  CardDetails,
  InvoiceSettingsResponse,
  UserAgreementsResponse,
  VerificationInitResponse,
} from "./types";
import { State } from "../../../selectors/state";

const CardManagement: FC = () => {
  const [cards, setCards] = useState<CardDetails[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [verificationInProgress, setVerificationInProgress] = useState(false);
  const [autoPaymentEnabled, setAutoPaymentEnabled] = useState(false);

  const userlogin = useSelector((state: State) => state.login?.userlogin);

  const fetchInvoiceSettings = useCallback(async () => {
    try {
      const response =
        (await FetchAPI.Account.getSettings()) as InvoiceSettingsResponse;
      const settings = response?.data?.settings;
      setAutoPaymentEnabled(settings?.automatic_payment === "t");
    } catch (error) {
      toastError(error, "Failed to load invoice settings");
    }
  }, []);

  const fetchUserAgreements = useCallback(async () => {
    setIsLoading(true);
    try {
      const response =
        (await FetchAPI.Payment.SwedBank.getUserAgreements()) as UserAgreementsResponse;
      setCards(response.data || []);
      if (response.data.length === 0) {
        toast.warn("Sorry! You don't have cards beyond this.");
      }
    } catch (error) {
      toastError(error, "Failed to load cards");
    } finally {
      setIsLoading(false);
    }
  }, []);

  const initVerification = useCallback(() => {
    const verificationObject = {
      payment: {
        user_agent: getUserAgent(),
        language: userlogin?.language ?? "en",
        urls: {
          return: `${window.location.origin}/settings/manageinvoicesettings?authr=1&authm=auth`,
          cancel: `${window.location.origin}/settings/manageinvoicesettings?authc=1&authm=auth`,
        },
      },
    };

    setVerificationInProgress(true);

    FetchAPI.Payment.SwedBank.creditCardVerificationInit(
      "card_verification",
      verificationObject,
    )
      .then((res) => {
        const responseData = res as VerificationInitResponse;
        toast.success("Wait for redirection to credit card provider...");
        setTimeout(() => {
          window.location.href = responseData.data.redirect_url;
        }, 2000);
      })
      .catch((err) => {
        toastError(err, "A temporary error occurred - please try again later.");
        setVerificationInProgress(false);
      });
  }, [userlogin.language]);

  useEffect(() => {
    const query = new URLSearchParams(window.location.search);
    const authValue = query.get("authr");
    const authMethod = query.get("authm");
    const authRef = query.get("ref");

    if (authMethod === "auth" && authValue === "1" && authRef) {
      FetchAPI.Payment.SwedBank.creditCardVerificationCheck(authRef)
        .then(() => {
          toast.success("Credit card successfully verified and added!");
          fetchUserAgreements();
          const currentUrl = window.location.href.split("?")[0];
          window.history.replaceState({}, document.title, currentUrl);
        })
        .catch((err) => {
          toastError(err, "Error during card verification.");
        })
        .finally(() => setVerificationInProgress(false));
    } else {
      fetchInvoiceSettings();
      fetchUserAgreements();
    }
  }, [fetchUserAgreements, fetchInvoiceSettings]);

  return (
    <div className="card-management margin-top-20">
      {autoPaymentEnabled ? (
        <Message positive>
          <Message.Header>Auto Payment Activated</Message.Header>
          <p>
            Any invoice will be automatically paid using your registered card.
            To change settings, visit the settings tab.
          </p>
        </Message>
      ) : cards.length > 0 ? (
        <Message negative>
          <Message.Header>Auto Payment Disabled</Message.Header>
          <p>
            You have the following credit cards defined. Auto payment is
            currently disabled. To enable it, visit the settings tab.
          </p>
        </Message>
      ) : null}

      {isLoading ? (
        <Loader active inline="centered" />
      ) : cards.length ? (
        <Grid stackable>
          {cards.map((card, index) => (
            <Grid.Column width={4} key={card.id}>
              <Card
                fluid
                className={index === 0 ? "card-management--active" : ""}
              >
                <Card.Content>
                  <Card.Header className="text-center">
                    <Icon name="credit card" className="margin-right-10" />
                    {card.masked_card_number}
                  </Card.Header>
                  <Card.Meta>Expires: {card.expire_date}</Card.Meta>
                  <Card.Description>
                    Currency: {card.currency_code}
                  </Card.Description>
                </Card.Content>
              </Card>
            </Grid.Column>
          ))}
        </Grid>
      ) : (
        <p>
          You currently have no credit card registered with us. Click the button
          below to add a new card.
        </p>
      )}
      <div className="flex margin-top-20">
        <button
          className="button button--green"
          onClick={initVerification}
          disabled={verificationInProgress}
        >
          {verificationInProgress ? "Adding Card..." : "Add New Card"}
        </button>
      </div>
    </div>
  );
};

export default CardManagement;
